import type { PersonSortKey, PersonStatus } from '@sentra/contracts';
import { personSortKeySchema, personStatusSchema, presenceStateSchema } from '@sentra/contracts';

/**
 * The register's view, as URL parameters.
 *
 * **A view is a thing people send each other.** "The blocked people who are still inside, sorted by
 * last activity" is a question somebody asks a colleague, and the honest way to answer it is a link
 * rather than a list of steps. That is the whole reason the state lives in the URL: it survives a
 * reload, it survives the back button, and it can be pasted into a message.
 *
 * **Everything here is a pure function over `URLSearchParams`.** The rule this enforces is that the
 * URL and the table say the same thing, and a round trip is the only way to check that: encode,
 * decode, and get back what you started with. A property enforced inside a component is one nobody
 * can test without a browser.
 *
 * **A parameter this module does not recognise is dropped rather than trusted.** The values come
 * from somebody else's link, so a `status=deleted` or a `sort=notes` must not reach a query string
 * the server then refuses, and a `page=-4` must not produce a page that renders nothing with no
 * explanation. Every narrowing and the ordering are now the SERVER's, which makes that rule
 * load-bearing rather than tidy: an unrecognised value here is a 400 one screen later.
 *
 * **`page` is 1-based, the way the server counts and the way the screen says it.** It was an index
 * while the table paged its own rows; two counts for one number across a client and a server is an
 * off-by-one waiting to be written.
 *
 * **What is deliberately NOT here is the search term.** The register's box is where an operator
 * types a name, a passport number or a telephone to find somebody, and in a browser this URL is the
 * address bar: putting it here writes personal data into history — a file on a shared terminal's
 * disk, outside the encryption boundary, rotated rather than shredded — and into any link somebody
 * pastes into a message. `duplicateCandidatesRequestSchema` states the same argument as the reason
 * that guard is a POST rather than a GET. A shareable "view" is the SHAPE of the table: what is
 * filtered, how it is sorted, which columns are shown, how tall the rows are, which page. The term
 * lives in the screen's own state and survives a reload the way an unsaved form does: not at all,
 * which is the right answer for somebody else's name.
 */

/** How rows are ordered. `null` is the register's own order: the displayed name, then the reference. */
interface SortState {
  readonly columnId: PersonSortKey;
  readonly descending: boolean;
}

/** How much of the screen a row takes. */
export type Density = 'comfortable' | 'compact';

/** Whether a row must have a photograph on file. */
type PhotoFilter = 'any' | 'with' | 'without';

/** Everything the register's screen remembers about how it is being looked at. */
export interface RegisterView {
  /** The status filter, which the projection answers without decrypting anything. */
  readonly status: PersonStatus | null;
  /** The presence filter, answered from the cleartext lifecycle column over the whole register. */
  readonly presence: 'inside' | 'outside' | null;
  /** Whether a photograph is on file, answered the same way and over the same population. */
  readonly photo: PhotoFilter;
  readonly sort: SortState | null;
  readonly hidden: readonly string[];
  readonly density: Density;
  /** Which page, 1-based. `1` is the first page and is omitted from the URL. */
  readonly page: number;
}

/** An empty register: nothing searched for, nothing filtered, nothing hidden. */
export const DEFAULT_VIEW: RegisterView = {
  status: null,
  presence: null,
  photo: 'any',
  sort: null,
  hidden: [],
  density: 'comfortable',
  page: 1,
};

/** The parameter names, in one place, so the encoder and the decoder cannot disagree. */
const PARAM = {
  status: 'status',
  presence: 'presence',
  photo: 'photo',
  sort: 'sort',
  hidden: 'hide',
  density: 'density',
  page: 'page',
} as const;

function parsePage(raw: string | null): number {
  const page = Number.parseInt(raw ?? '', 10);
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

function parseSort(raw: string | null): SortState | null {
  if (raw === null || raw.length === 0) {
    return null;
  }
  // `-columnId` for descending, matching the shape every sortable list in every API uses, so a
  // person reading the URL does not have to look anything up.
  const descending = raw.startsWith('-');
  // Checked against the contract's own set rather than against the table's columns: this value is
  // sent to the server, which refuses anything else, and `personSortKeySchema` is the one place
  // that decides what the register can be ordered by.
  const columnId = personSortKeySchema.safeParse(descending ? raw.slice(1) : raw);
  return columnId.success ? { columnId: columnId.data, descending } : null;
}

function parsePhoto(raw: string | null): PhotoFilter {
  return raw === 'with' || raw === 'without' ? raw : 'any';
}

/** The view a URL describes. Anything unrecognised falls back to the default rather than through. */
export function viewFromParams(params: URLSearchParams): RegisterView {
  const status = personStatusSchema.safeParse(params.get(PARAM.status) ?? '');
  const presence = presenceStateSchema.safeParse(params.get(PARAM.presence) ?? '');
  const hidden = (params.get(PARAM.hidden) ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

  return {
    status: status.success ? status.data : null,
    presence: presence.success ? presence.data : null,
    photo: parsePhoto(params.get(PARAM.photo)),
    sort: parseSort(params.get(PARAM.sort)),
    hidden,
    density: params.get(PARAM.density) === 'compact' ? 'compact' : 'comfortable',
    page: parsePage(params.get(PARAM.page)),
  };
}

/**
 * The URL a view describes.
 *
 * A default is OMITTED rather than written out, so an untouched register has a clean URL and a
 * shared link carries only what somebody actually chose. It is also what makes the round-trip test
 * meaningful: if every default were written, the encoder would agree with the decoder by writing
 * everything down rather than by agreeing about anything.
 */
export function paramsFromView(view: RegisterView): URLSearchParams {
  const params = new URLSearchParams();
  if (view.status !== null) {
    params.set(PARAM.status, view.status);
  }
  if (view.presence !== null) {
    params.set(PARAM.presence, view.presence);
  }
  if (view.photo !== 'any') {
    params.set(PARAM.photo, view.photo);
  }
  if (view.sort !== null) {
    params.set(PARAM.sort, `${view.sort.descending ? '-' : ''}${view.sort.columnId}`);
  }
  if (view.hidden.length > 0) {
    params.set(PARAM.hidden, [...view.hidden].sort((a, b) => (a < b ? -1 : 1)).join(','));
  }
  if (view.density !== 'comfortable') {
    params.set(PARAM.density, view.density);
  }
  if (view.page !== 1) {
    params.set(PARAM.page, String(view.page));
  }
  return params;
}
